/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  input,
  output,
  signal,
  PLATFORM_ID,
} from '@angular/core';

import { AdditionalData } from '@shared/data-access/interfaces';
import { BypassSanitizationPipe } from '@shared/utils/pipes';
import { HomeDirectiveModule } from '@home/utils';
import { KrakenService } from './kraken.service';
import { WINDOW } from '@shared/data-access/services';
import { take } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'shared-components-engage-kraken',
  templateUrl: './kraken.component.html',
  styleUrl: './kraken.component.scss',
  imports: [HomeDirectiveModule, BypassSanitizationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KrakenComponent implements OnInit {
  private readonly krakenService = inject(KrakenService);
  private readonly window = inject(WINDOW);
  private readonly platformId = inject(PLATFORM_ID);

  readonly data = input.required<Record<string, string>[] | null>();
  readonly addlData = input.required<AdditionalData>();

  readonly loadEvent = output<{ isError: boolean }>();

  readonly krakenContent = signal<string>('');

  get isIE() {
    return (
      this.window.navigator.userAgent.indexOf('MSIE ') > 0 ||
      !!/Trident.*rv:11\./.exec(navigator.userAgent)
    );
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const krakenPageName = this.data()?.[0]?.pageName;
      if (krakenPageName) {
        this.krakenService
          .get(krakenPageName)
          .pipe(take(1))
          .subscribe({
            next: (content) => {
              this.handleKrakenContentResponse(krakenPageName, content);
            },
            error: () => {
              this.loadEvent.emit({ isError: true });
            },
          });
      }
    }
  }

  public handleKrakenContentResponse(pageName: string, content: string) {
    if (content?.includes('href="#')) {
      content = this.anchorLinkFix(content);
    }
    if (content?.includes('<script') && !this.isIE) {
      content = this.blockScripts(content);
    }
    this.loadEvent.emit({ isError: false });
    this.krakenContent.set(`<!-- BEGIN - ${pageName}-content -->
			${content}
			<!-- END - ${pageName}-content -->`);
  }

  public anchorLinkFix(content: string) {
    const splitContent = content.split('href="#');
    const revisedContent = [];
    const pageUrl = this.window.location.pathname;
    for (let i = 0; i < splitContent.length; i++) {
      const item = splitContent[i];
      if (i > 0) {
        revisedContent.push(`href="${pageUrl}#${item}`);
      } else {
        revisedContent.push(item);
      }
    }
    return revisedContent.join('');
  }

  public blockScripts(content: string) {
    const tmpDiv = window.document.createElement('div');
    tmpDiv.id = 'tmpScriptHolder';
    tmpDiv.innerHTML = content;
    const scripts = tmpDiv.querySelectorAll('script');
    for (let i = 0; i < scripts.length; i++) {
      const v = scripts[i];
      v.type = !['text/x-handlebars-template'].includes(v.type) ? 'iliad/blocked' : v.type;
    }
    const response = tmpDiv.innerHTML;
    tmpDiv.remove();
    return response;
  }
}
